import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from 'src/@/components/ui/dropdown-menu';
import { resolvePlatform } from 'src/design/tokens';
import { MKT_POSTS_DESIGN as d } from './MktPosts.design';
import { STATUS_MAP } from './constants';

// Quantos cards por leva de carregamento sob demanda.
const PAGE_SIZE = 24;

function truncate(text, max = 90) {
    if (!text) return '';
    return text.length > max ? `${text.slice(0, max)}...` : text;
}

function formatDate(dateStr, locale) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatNumber(num) {
    if (!num) return '0';
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return String(num);
}

export default function MktPostsGrid({ posts, capabilities, onOpen, onPreview, onDelete, onPublish, onRetry, onCancel, selectedIds, onToggleOne }) {
    const { t, i18n } = useTranslation('marketing');
    const canPublishAny = capabilities?.canPublishAny !== false;

    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const sentinelRef = useRef(null);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [posts]);

    const hasMore = visibleCount < posts.length;

    useEffect(() => {
        if (!hasMore) return undefined;
        const node = sentinelRef.current;
        if (!node) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, posts.length));
                }
            },
            { rootMargin: '400px' }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, posts.length]);

    const visiblePosts = posts.slice(0, visibleCount);

    return (
        <>
        <div className={d.postGrid.grid}>
            {visiblePosts.map((post) => {
                const status = STATUS_MAP[post.status] || STATUS_MAP.draft;
                const media = post.content?.media || [];
                const thumb = media[0];
                const isGraphImport = post.source === 'graph';
                const displayDate = post.published_date || post.scheduled_date;
                const canRetry = post.status === 'failed' || post.status === 'partially_published';
                const canPublishNow = ['draft', 'scheduled', 'failed'].includes(post.status);
                const canCancel = post.status === 'scheduled';
                const caption = truncate(post.content?.text);

                return (
                    <div key={post._id} className={d.postGrid.card} onClick={() => (onPreview || onOpen)(post)}>
                        <div className={d.postGrid.thumbWrap}>
                            <div className="absolute top-2 left-2 z-10" onClick={(e) => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 text-[#7C3AED] border-gray-300 rounded cursor-pointer shadow"
                                    checked={selectedIds.has(post._id)}
                                    onChange={() => onToggleOne(post._id)}
                                />
                            </div>
                            {thumb ? (
                                thumb.type === 'video' ? (
                                    // eslint-disable-next-line jsx-a11y/media-has-caption
                                    <video src={`${thumb.url}#t=0.1`} preload="metadata" className={d.postGrid.thumb} />
                                ) : (
                                    <img src={thumb.url} alt="" className={d.postGrid.thumb} />
                                )
                            ) : (
                                <div className={d.postGrid.thumbPlaceholder}>
                                    <Iconify icon="eva:file-text-outline" width={28} />
                                </div>
                            )}

                            <span className={`${d.postGrid.statusPill} ${status.tone === 'green' ? 'bg-green-600/80' : status.tone === 'red' ? 'bg-red-600/80' : status.tone === 'amber' ? 'bg-amber-600/80' : status.tone === 'blue' ? 'bg-blue-600/80' : 'bg-black/55'}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                {t(status.labelKey)}
                            </span>

                            <div className={d.postGrid.platformRow}>
                                {(post.platforms || []).map((p) => {
                                    const info = resolvePlatform(p);
                                    if (!info) return null;
                                    return (
                                        <span key={p} className={d.postGrid.platformIcon} title={info.label}>
                                            <Iconify icon={info.icon} width={13} />
                                        </span>
                                    );
                                })}
                            </div>

                            {isGraphImport && (
                                <span className={d.postGrid.importedBadge}>
                                    <Iconify icon="eva:download-outline" width={10} />
                                    {t('posts.table.imported')}
                                </span>
                            )}

                            <div onClick={(e) => e.stopPropagation()}>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button type="button" className={d.postGrid.menuBtn}>
                                            <Iconify icon="eva:more-vertical-outline" width={16} />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56 rounded-xl">
                                        <DropdownMenuItem onClick={() => onOpen(post)}>
                                            <Iconify icon={isGraphImport ? 'eva:eye-outline' : 'eva:edit-2-outline'} width={16} className="mr-2" />
                                            {isGraphImport ? t('posts.menu.viewDetails') : t('posts.menu.edit')}
                                        </DropdownMenuItem>
                                        {canPublishNow && (
                                            <DropdownMenuItem onClick={() => onPublish(post)} disabled={!canPublishAny}>
                                                <Iconify icon="eva:paper-plane-outline" width={16} className="mr-2" />
                                                {t('posts.menu.publishNow')}
                                            </DropdownMenuItem>
                                        )}
                                        {canRetry && (
                                            <DropdownMenuItem onClick={() => onRetry(post)} disabled={!canPublishAny}>
                                                <Iconify icon="eva:refresh-outline" width={16} className="mr-2" />
                                                {t('posts.menu.retry')}
                                            </DropdownMenuItem>
                                        )}
                                        {canCancel && (
                                            <DropdownMenuItem onClick={() => onCancel(post)}>
                                                <Iconify icon="eva:slash-outline" width={16} className="mr-2" />
                                                {t('posts.menu.cancelSchedule')}
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => onDelete(post)} className="text-red-600 focus:text-red-600">
                                            <Iconify icon="eva:trash-2-outline" width={16} className="mr-2" />
                                            {t('posts.menu.delete')}
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>

                        <div className={d.postGrid.body}>
                            {caption ? (
                                <p className={d.postGrid.caption}>{caption}</p>
                            ) : (
                                <p className={d.postGrid.captionEmpty}>{t('posts.table.noText')}</p>
                            )}
                            <div className={d.postGrid.footer}>
                                <span className={d.postGrid.date}>{formatDate(displayDate, i18n.language)}</span>
                                {post.status === 'published' && post.performance ? (
                                    <span className={d.postGrid.perf} title={t('posts.table.likes')}>
                                        <Iconify icon="eva:heart-outline" width={11} />
                                        {formatNumber(post.performance.likes)}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>

        {hasMore && (
            <div ref={sentinelRef} className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-violet-200 dark:border-violet-500/30 border-t-violet-600 dark:border-t-violet-400 rounded-full animate-spin" />
            </div>
        )}
        </>
    );
}
